import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useParams, Navigate } from 'react-router';
import { useSegment } from '@repo/poly-hooks/segment';
import { PaymentMethods } from '@repo/domain-kit/shared';
import { ErrorState } from '@repo/design-system-kit';
import { ResponsiveView } from 'receivable-invoices-js/react/components/shared/responsive-view';
import { useMollie } from 'receivable-invoices-js/react/hooks/use-mollie';
import { useLocaleManager } from 'receivable-invoices-js/react/hooks/use-locale-manager';
import { Loading } from 'receivable-invoices-js/react/components/shared/loading';
import { useRouteQuery } from './use-route-query';
import styles from './layout.module.css';

export function InvoicesIndexRoute(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const segment = useSegment();
  const { formatMessage } = useIntl();
  const mollie = useMollie();
  const localeManager = useLocaleManager();
  const {
    invoice,
    paymentLink,
    attachments,
    isClientPortalEnabled,
    isLoading,
    isError,
    paymentPollingStatus,
  } = useRouteQuery();

  useEffect(() => {
    if (invoice?.locale) {
      localeManager.setLocale(invoice.locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- localeManager is a stable reference from useEmberService
  }, [invoice]);

  useEffect(() => {
    if (invoice) {
      if (invoice.isEinvoice) {
        segment.track('public-page-invoice_loaded', {
          property: 'einvoicing',
        });
      } else {
        segment.track('public-page-invoice_loaded');
      }
    }
  }, [invoice, segment]);

  // Load Mollie if needed
  useEffect(() => {
    const hasPaymentMethodsOtherThanBankTransfer = paymentLink?.potentialPaymentMethods.some(
      method => method !== PaymentMethods.BankTransfer
    );

    const shouldLoadMollie = paymentLink?.profileId && hasPaymentMethodsOtherThanBankTransfer;

    if (shouldLoadMollie) {
      void mollie.load(paymentLink.profileId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mollie is a stable reference from useMollie hook
  }, [paymentLink]);

  const handleRetryMollieError = () => {
    window.location.reload();
  };

  if (isLoading) {
    return <Loading spinnerSize="large" />;
  }

  if (isError || !invoice) {
    return <Navigate replace to={`/invoices/${id}/not-found`} />;
  }

  if (mollie.hasError) {
    return (
      <div className={styles.errorStateContainer}>
        <ErrorState
          onRetry={handleRetryMollieError}
          subtitle={formatMessage({ id: 'error-states.mollie-activation.legend' })}
          title={formatMessage({ id: 'error-states.mollie-activation.title' })}
        />
      </div>
    );
  }

  return (
    <ResponsiveView
      attachments={attachments}
      data-test-invoice-responsive-view=""
      document={invoice}
      isClientPortalEnabled={isClientPortalEnabled}
      paymentLink={paymentLink}
      paymentPollingStatus={paymentPollingStatus}
    />
  );
}
